/**
 * HostRoute — reicht einer Modul-Seite den Host-Bausatz (`host`-Prop) aus dem Portal.
 *
 * Dokument-Navigation Scheibe 3 (Abhakliste 3.01, Jörg 2026-09-29): Module ohne eigenen Router
 * (faktura, kalkulation, wissen, fleet, Skelett-Module) bekommen Adresse, Parameter, Navigation,
 * Zurück im Verlauf, Verlassen-Schutz, Rückfragen sowie die Bausteine und Hooks aus
 * `@onvice/portal-ui` über EIN Prop. Vertrag: `@onvice/shared-types` `module-host-kit.ts`.
 *
 * Die Komponenten und Hooks des Portals laufen im Modul in derselben React-Instanz (React ist
 * Federation-Singleton) — deshalb funktionieren Router-Kontext und Verlassen-Schutz dort ohne
 * eigene Abhängigkeit des Moduls.
 */
import { useCallback, useMemo, type ComponentType } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { registerDirtySource } from '@onvice/portal-shell-api';
import {
  BackLink, ConfirmModal, DocumentDialog, DocumentHeader, EditBar, GuardedModal, ParentLinks,
  SaveFeedback, SettingsSection, UnsavedChangesModal,
  dialogs, param, parentRef,
  useAnchorScroll, useBack, useChangeMap, useDirtySnapshot, useEditSession, useGuardedClose,
  useListState, useModuleAccess, usePageLabel, useScrollRestore, useViewMemory,
} from '@onvice/portal-ui';
import type {
  HostBackLinkProps, HostBackOptions, HostDocumentHeaderProps, HostListSchema, HostListValues,
  HostListStateControls, ModuleHostKit, ModuleHostProps,
} from '@onvice/shared-types';

interface Props {
  basePath: string;
  Component: ComponentType<ModuleHostProps>;
}

/** Modulrelative Adresse → Portal-Pfad. `/m/…`, `/admin/…` usw. bleiben absolut. */
function absolute(basePath: string, to: string): string {
  if (/^(\/m\/|\/admin\/|\/uebersicht|\/kalender|\/todos|\/suche|\/genehmigungen|\/einstellungen)/.test(to)) return to;
  if (to === '' || to === '/') return basePath;
  return `${basePath}/${to.replace(/^\/+/, '')}`;
}

export default function HostRoute({ basePath, Component }: Props) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const access = useModuleAccess();

  const path = location.pathname.startsWith(basePath) ? (location.pathname.slice(basePath.length) || '/') : location.pathname;

  const setSearch = useCallback((patch: Record<string, string | null | undefined>) => {
    // Vom Live-Stand der Adresse ausgehen (siehe useListState, Fund 7 vom 01.10.).
    const next = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    }
    setSearchParams(next, { replace: true, preventScrollReset: true });
  }, [setSearchParams]);

  // Bausteine mit modulrelativem `fallback` — einmal je Modul erzeugt, sonst würden sie bei
  // jedem Rendern neu eingehängt.
  const ui = useMemo<ModuleHostKit['ui']>(() => ({
    BackLink: (p: HostBackLinkProps) => <BackLink {...p} fallback={absolute(basePath, p.fallback)} />,
    ParentLinks,
    DocumentHeader: (p: HostDocumentHeaderProps) => <DocumentHeader {...p} back={{ ...p.back, fallback: absolute(basePath, p.back.fallback) }} />,
    EditBar,
    SaveFeedback,
    UnsavedChangesModal,
    GuardedModal,
    DocumentDialog,
    SettingsSection,
    ConfirmModal,
  }), [basePath]);

  const hooks = useMemo<ModuleHostKit['hooks']>(() => ({
    useEditSession,
    useGuardedClose,
    useDirtySnapshot,
    useChangeMap,
    useListState: <S extends HostListSchema>(schema: S) =>
      useListState(schema) as unknown as [HostListValues<S>, (patch: Partial<HostListValues<S>>) => void, HostListStateControls],
    useViewMemory,
    useScrollRestore: (ready: boolean) => useScrollRestore(ready),
    useAnchorScroll,
    usePageLabel,
    useBack: (o: HostBackOptions) => useBack({ fallback: absolute(basePath, o.fallback), fallbackLabel: o.fallbackLabel }),
  }), [basePath]);

  const kit = useMemo<ModuleHostKit>(() => ({
    basePath,
    path,
    params: Object.fromEntries(Object.entries(params).filter((e): e is [string, string] => typeof e[1] === 'string' && e[0] !== '*' && e[0] !== 'moduleSlug')),
    search: searchParams,
    setSearch,
    navigate: (to, opts) => navigate(absolute(basePath, to), { replace: opts?.replace === true }),
    href: (to) => absolute(basePath, to),
    canOpenModule: access.has,
    registerDirtySource,
    dialogs,
    param,
    parentRef,
    hooks,
    ui,
  }), [basePath, path, params, searchParams, setSearch, navigate, access.has, hooks, ui]);

  return <Component host={kit} />;
}
